// Gamification Components: XP, Levels, Badges, Streaks, Heatmap
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Trophy, Flame, Star, TrendingUp, Award, Zap } from "lucide-react";
import { getLevelRank, formatRankDisplay } from "@/lib/rankSystem";

// ====================================
// TYPES
// ====================================
interface UserXP {
  total_xp: number;
  current_level: number;
  xp_to_next_level: number;
  lifetime_xp: number;
}

interface Achievement {
  id: string;
  achievement_type: string;
  badge_level?: string;
  earned_at: string;
  metadata?: Record<string, any>;
}

interface PracticeDay {
  date: string;
  count: number;
  problems: number;
}

// ====================================
// XP PROGRESS BAR
// ====================================
export function XPProgressBar({ xp }: { xp: UserXP | null }) {
  if (!xp) return null;

  const progress = (xp.total_xp / xp.xp_to_next_level) * 100;
  const rank = getLevelRank(xp.current_level);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          <span className="font-semibold text-gray-900 dark:text-white">
            {rank.icon} {formatRankDisplay(xp.current_level)}
          </span>
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {xp.total_xp} / {xp.xp_to_next_level} XP
        </span>
      </div>

      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 transition-all duration-500 ease-out"
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {Math.floor(xp.xp_to_next_level - xp.total_xp)} XP to Level{" "}
        {xp.current_level + 1}
      </p>
    </div>
  );
}

// ====================================
// STREAK COUNTER
// ====================================
export function StreakCounter({
  currentStreak,
  longestStreak,
}: {
  currentStreak: number;
  longestStreak: number;
}) {
  const getStreakColor = (streak: number) => {
    if (streak >= 30) return "text-purple-600 dark:text-purple-400";
    if (streak >= 7) return "text-orange-600 dark:text-orange-400";
    return "text-red-600 dark:text-red-400";
  };

  const getFlameIcon = (streak: number) => {
    if (streak >= 30) return "🔥🔥🔥";
    if (streak >= 7) return "🔥🔥";
    return "🔥";
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Flame className={`w-6 h-6 ${getStreakColor(currentStreak)}`} />
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {currentStreak}
            </span>
            <span className="text-sm">{getFlameIcon(currentStreak)}</span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Discipline Streak
          </p>
        </div>

        <div className="text-right">
          <div className="text-xl font-semibold text-gray-700 dark:text-gray-300">
            {longestStreak}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Best Streak
          </p>
        </div>
      </div>

      {currentStreak > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {currentStreak === 1 && "You're on a roll! Keep it up!"}
            {currentStreak >= 2 &&
              currentStreak < 7 &&
              "Great start! Can you reach 7 days?"}
            {currentStreak >= 7 &&
              currentStreak < 30 &&
              "Amazing! You're building a habit! 🎯"}
            {currentStreak >= 30 && "Legendary dedication! 🏆"}
          </p>
        </div>
      )}
    </div>
  );
}

// ====================================
// ACHIEVEMENT BADGE
// ====================================
export function AchievementBadge({
  achievement,
}: {
  achievement: Achievement;
}) {
  const getBadgeColor = (level?: string) => {
    switch (level) {
      case "platinum":
        return "bg-gradient-to-br from-cyan-400 to-blue-600";
      case "gold":
        return "bg-gradient-to-br from-yellow-400 to-yellow-600";
      case "silver":
        return "bg-gradient-to-br from-gray-300 to-gray-500";
      case "bronze":
        return "bg-gradient-to-br from-amber-600 to-amber-800";
      default:
        return "bg-gradient-to-br from-blue-400 to-blue-600";
    }
  };

  const getAchievementName = (type: string) => {
    const names: Record<string, string> = {
      technique_bronze: "Bronze Mastery",
      technique_silver: "Silver Mastery",
      technique_gold: "Gold Mastery",
      technique_platinum: "Platinum Mastery",
      speed_demon: "Speed Demon",
      perfect_session: "Perfect Session",
      streak_7: "7-Day Streak",
      streak_30: "30-Day Streak",
      streak_100: "100-Day Streak",
      early_bird: "Early Bird",
      night_owl: "Night Owl",
      weekend_warrior: "Weekend Warrior",
      module_complete: "Module Complete",
      first_lesson: "First Steps",
      problem_solver_100: "100 Problems",
      problem_solver_500: "500 Problems",
      problem_solver_1000: "1000 Problems",
    };
    return names[type] || type;
  };

  return (
    <div className="flex flex-col items-center group cursor-pointer">
      <div
        className={`w-16 h-16 rounded-full ${getBadgeColor(achievement.badge_level)} flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform`}
      >
        <Award className="w-8 h-8 text-white" />
      </div>
      <p className="text-xs text-center mt-2 text-gray-700 dark:text-gray-300 font-medium">
        {getAchievementName(achievement.achievement_type)}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {new Date(achievement.earned_at).toLocaleDateString()}
      </p>
    </div>
  );
}

// ====================================
// ACHIEVEMENTS SHOWCASE
// ====================================
export function AchievementsShowcase() {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("achievements")
      .select("*")
      .eq("user_id", user.id)
      .order("earned_at", { ascending: false });

    if (!error && data) {
      setAchievements(data);
    }
    setLoading(false);
  };

  if (loading) return <div>Loading achievements...</div>;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-5 h-5 text-yellow-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Achievements ({achievements.length})
        </h3>
      </div>

      {achievements.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          Complete training sessions to earn achievements!
        </p>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {achievements.map((achievement) => (
            <AchievementBadge key={achievement.id} achievement={achievement} />
          ))}
        </div>
      )}
    </div>
  );
}

// ====================================
// PRACTICE HEATMAP (GitHub-style)
// ====================================
export function PracticeHeatmap() {
  const [practiceData, setPracticeData] = useState<PracticeDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPracticeHistory();
  }, []);

  const fetchPracticeHistory = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    // Get last 365 days
    const oneYearAgo = new Date();
    oneYearAgo.setDate(oneYearAgo.getDate() - 365);

    const { data, error } = await supabase
      .from("practice_history")
      .select("practice_date, problems_attempted")
      .eq("user_id", user.id)
      .gte("practice_date", oneYearAgo.toISOString().split("T")[0])
      .order("practice_date", { ascending: true });

    if (!error && data) {
      setPracticeData(
        data.map((d) => ({
          date: d.practice_date,
          count: 1,
          problems: d.problems_attempted,
        })),
      );
    }
    setLoading(false);
  };

  const getColorForCount = (count: number, problems: number) => {
    if (count === 0) return "bg-gray-100 dark:bg-gray-800";
    if (problems >= 50) return "bg-green-600";
    if (problems >= 20) return "bg-green-500";
    if (problems >= 10) return "bg-green-400";
    return "bg-green-300";
  };

  // Generate last 365 days
  const generateDays = () => {
    const days: { date: string; count: number; problems: number }[] = [];
    const today = new Date();

    for (let i = 364; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];

      const practice = practiceData.find((p) => p.date === dateStr);
      days.push({
        date: dateStr,
        count: practice ? practice.count : 0,
        problems: practice ? practice.problems : 0,
      });
    }

    return days;
  };

  if (loading) return <div>Loading heatmap...</div>;

  const days = generateDays();
  const weeks: (typeof days)[] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm overflow-x-auto">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-blue-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Training Activity
        </h3>
      </div>

      <div className="flex gap-1">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="flex flex-col gap-1">
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className={`w-3 h-3 rounded-sm ${getColorForCount(day.count, day.problems)} hover:ring-2 hover:ring-blue-400 transition-all cursor-pointer`}
                title={`${day.date}: ${day.problems} problems`}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-4 text-xs text-gray-500 dark:text-gray-400">
        <span>Less</span>
        <div className="w-3 h-3 rounded-sm bg-gray-100 dark:bg-gray-800" />
        <div className="w-3 h-3 rounded-sm bg-green-300" />
        <div className="w-3 h-3 rounded-sm bg-green-400" />
        <div className="w-3 h-3 rounded-sm bg-green-500" />
        <div className="w-3 h-3 rounded-sm bg-green-600" />
        <span>More</span>
      </div>
    </div>
  );
}

// ====================================
// GAMIFICATION DASHBOARD WIDGET
// ====================================
export function GamificationWidget() {
  const [xp, setXp] = useState<UserXP | null>(null);
  const [streak, setStreak] = useState({ current: 0, longest: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGamificationData();
  }, []);

  const fetchGamificationData = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    // Fetch XP
    const { data: xpData } = await supabase
      .from("user_xp")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (xpData) {
      setXp(xpData);
    }

    // Fetch streak
    const { data: streakData } = await supabase
      .from("streaks")
      .select("current_streak, longest_streak")
      .eq("user_id", user.id)
      .maybeSingle();

    if (streakData) {
      setStreak({
        current: streakData.current_streak || 0,
        longest: streakData.longest_streak || 0,
      });
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-gray-200 dark:bg-gray-700 h-20 rounded-lg animate-pulse" />
        <div className="bg-gray-200 dark:bg-gray-700 h-20 rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <XPProgressBar xp={xp} />
      <StreakCounter
        currentStreak={streak.current}
        longestStreak={streak.longest}
      />
    </div>
  );
}

// ====================================
// LEVEL UP CELEBRATION
// ====================================
export function LevelUpCelebration({
  newLevel,
  onClose,
}: {
  newLevel: number;
  onClose: () => void;
}) {
  const rank = getLevelRank(newLevel);
  const prevRank = getLevelRank(newLevel - 1);
  const isRankUp = rank.key !== prevRank.key;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 text-center transform animate-scale-in">
        <div className="text-6xl mb-4">{isRankUp ? rank.icon : "🎉"}</div>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {isRankUp ? "Rank Up!" : "Level Up!"}
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
          {isRankUp ? (
            <>
              You've become a <span className="font-bold text-blue-600 dark:text-blue-400">{rank.name}</span>!
            </>
          ) : (
            <>You've reached {formatRankDisplay(newLevel)}!</>
          )}
        </p>
        {isRankUp && (
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg border-2 border-blue-300 dark:border-blue-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">New Rank</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{rank.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{rank.description}</p>
          </div>
        )}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Star className="w-8 h-8 text-yellow-500 animate-spin-slow" />
          <Star className="w-10 h-10 text-yellow-500" />
          <Star className="w-8 h-8 text-yellow-500 animate-spin-slow" />
        </div>
        <button
          onClick={onClose}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          Continue Training
        </button>
      </div>
    </div>
  );
}

// Add to globals.css for animations
/*
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scale-in {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}

.animate-scale-in {
  animation: scale-in 0.3s ease-out;
}

.animate-spin-slow {
  animation: spin-slow 3s linear infinite;
}
*/
